import { useMemo, useState } from 'react';
import { useDatasets } from '../../api/datasets.js';
import { useCreateReportTemplate } from '../../api/report-templates.js';
import type {
  HtmlLayout,
  ReportTemplateDefinition,
  ReportTemplateSummary,
  TabularColumn,
} from '../../api/types.js';
import type { FrameworkDetail } from '../../api/frameworks.js';
import { FrameworkPicker } from '../frameworks/FrameworkPicker.js';
import { stashStarterPrompt } from '../frameworks/starter-prompt-seed.js';

interface Props {
  onClose(): void;
  onCreated(t: ReportTemplateSummary): void;
}

export function NewTemplateDialog({ onClose, onCreated }: Props): JSX.Element {
  const { data: datasets, isLoading: dsLoading } = useDatasets();
  const create = useCreateReportTemplate();

  const [name, setName] = useState('');
  const [datasetId, setDatasetId] = useState<string | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
  const [manualColumns, setManualColumns] = useState('');
  const [frameworkId, setFrameworkId] = useState<string | null>(null);
  const [framework, setFramework] = useState<FrameworkDetail | null>(null);

  const dataset = useMemo(
    () => datasets?.find((d) => d.id === datasetId) ?? null,
    [datasets, datasetId],
  );

  const knownColumns = useMemo(() => {
    if (!dataset) return [] as string[];
    const declared = (dataset.columns ?? []) as Array<{ name: string }>;
    return declared.map((c) => c.name);
  }, [dataset]);

  // When a framework is picked, columns aren't required — the framework
  // supplies the HTML layout and the user iterates in the AI designer.
  const canSubmit =
    name.trim().length > 0 &&
    !!dataset &&
    (!!framework || selectedColumns.size > 0 || manualColumns.trim().length > 0) &&
    !create.isPending;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!dataset) return;

    let definition: ReportTemplateDefinition;
    if (framework && framework.skeleton.kind === 'html-handlebars') {
      // Seed v1 from the framework's HTML skeleton. The user refines in
      // the AI designer from a competent starting point. Report
      // templates are HTML-only today; widget frameworks are filtered
      // out at the picker so this branch always narrows cleanly.
      const sk = framework.skeleton;
      const columnNames = (dataset.columns ?? []).map((c: { name: string }) => c.name);
      const layout: HtmlLayout = {
        kind: 'html',
        title: name.trim(),
        html: sk.html,
        libraries: ['chartjs', 'lucide', 'fontawesome'],
        ...(columnNames.length > 0 ? { columnOrder: columnNames } : {}),
        ...(sk.xlsxSpec ? { xlsxSpec: sk.xlsxSpec as NonNullable<HtmlLayout['xlsxSpec']> } : {}),
      };
      definition = {
        schemaVersion: '1.0.0',
        datasetId: dataset.name,
        parameters: [],
        layout,
      };
    } else {
      const columnNames =
        selectedColumns.size > 0
          ? Array.from(selectedColumns)
          : manualColumns
              .split(/[,\n]/)
              .map((s) => s.trim())
              .filter(Boolean);

      const columns: TabularColumn[] = columnNames.map((c) => ({
        field: c,
        label: humanizeColumn(c),
        align: guessAlign(c),
      }));

      definition = {
        schemaVersion: '1.0.0',
        datasetId: dataset.name,
        parameters: [],
        layout: { kind: 'tabular', title: name.trim(), columns },
      };
    }

    try {
      const created = await create.mutateAsync({ name: name.trim(), definition });
      // If the user picked a framework, hand off its starterPrompt so
      // the designer's chat opens with it pre-populated.
      if (framework?.skeleton.starterPrompt) {
        stashStarterPrompt('report-template', created.id, framework.skeleton.starterPrompt);
      }
      onCreated(created);
    } catch {
      /* handled inline */
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <h2>New report template</h2>
            <div className="modal-subtitle">Pick a dataset, choose columns, publish as v1.</div>
          </div>
          <button className="btn-icon" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <form onSubmit={submit}>
          <div className="modal-body stack">
            <FrameworkPicker
              moduleType="report"
              skeletonKind="html-handlebars"
              value={frameworkId}
              onChange={(id) => {
                setFrameworkId(id);
                if (id === null) setFramework(null);
              }}
              onSkeletonLoaded={(detail) => setFramework(detail)}
            />

            <label className="field">
              <span className="field-label">Template name</span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Device Health Summary"
                autoFocus
              />
            </label>

            <label className="field">
              <span className="field-label">Dataset</span>
              {dsLoading ? (
                <div className="text-dim text-sm">Loading datasets…</div>
              ) : !datasets || datasets.length === 0 ? (
                <div className="alert alert--warning">
                  No datasets yet. Register a data source and sync its catalog first.
                </div>
              ) : (
                <select
                  className="select"
                  value={datasetId ?? ''}
                  onChange={(e) => {
                    setDatasetId(e.target.value || null);
                    setSelectedColumns(new Set());
                    setManualColumns('');
                  }}
                >
                  <option value="">Select a dataset…</option>
                  {datasets.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.mode})
                    </option>
                  ))}
                </select>
              )}
            </label>

            {dataset && !framework && (
              <div className="field">
                <span className="field-label">Columns</span>
                {knownColumns.length > 0 ? (
                  <div className="chip-group">
                    {knownColumns.map((col) => {
                      const selected = selectedColumns.has(col);
                      return (
                        <button
                          key={col}
                          type="button"
                          className={'chip' + (selected ? ' chip--on' : '')}
                          onClick={() =>
                            setSelectedColumns((prev) => {
                              const next = new Set(prev);
                              if (next.has(col)) next.delete(col);
                              else next.add(col);
                              return next;
                            })
                          }
                        >
                          {col}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <div className="field-help" style={{ marginBottom: 6 }}>
                      Column list hasn't been introspected yet. Enter names manually — comma or
                      newline-separated. They'll be validated against the dataset on first run.
                    </div>
                    <textarea
                      className="textarea"
                      rows={3}
                      value={manualColumns}
                      onChange={(e) => setManualColumns(e.target.value)}
                      placeholder="location_name, status, device_count"
                    />
                  </>
                )}
              </div>
            )}

            {create.error && (
              <div className="alert alert--danger">
                <div>
                  <strong>Create failed</strong>
                  <pre>
                    {JSON.stringify(
                      (create.error as Error & { body?: unknown }).body ?? String(create.error),
                      null,
                      2,
                    )}
                  </pre>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
              {create.isPending ? 'Creating…' : 'Create template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function humanizeColumn(name: string): string {
  return name
    .replace(/[._]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(' ');
}

function guessAlign(name: string): 'left' | 'center' | 'right' {
  if (/^(n|count|total|sum|avg|min|max|amount|qty|quantity|number|num)$/i.test(name))
    return 'right';
  if (/_count$|_total$|_sum$|_amt$|percent/i.test(name)) return 'right';
  return 'left';
}
